import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { a, useSpring } from "@react-spring/three";
import { InteractiveItem } from "../src/interactions/InteractiveItem";

const DEFAULT_HEIGHT = 3.5;
const DEFAULT_ASPECT = 16 / 9;
const LOOK_LERP_FACTOR = 0.25;

export default function CollectibleVideoPlane({
  id = "collectible-item",
  position = [0, 0, 0],
  height = DEFAULT_HEIGHT,
  radius = 5,
  targetRef = null,
  scaleMultiplier = 1,
  videoSrc,
  onCollect,
}) {
  const meshRef = useRef(null);
  const videoRef = useRef(null);
  const [videoTexture, setVideoTexture] = useState(null);
  const [aspect, setAspect] = useState(DEFAULT_ASPECT);
  const [collected, setCollected] = useState(false);
  const [consumed, setConsumed] = useState(false);

  const worldPos = useMemo(() => new THREE.Vector3(), []);
  const targetPos = useMemo(() => new THREE.Vector3(), []);
  const lookDirection = useMemo(() => new THREE.Vector3(), []);
  const cameraWorldPos = useMemo(() => new THREE.Vector3(), []);

  const camera = useThree((state) => state.camera);

  const handleCollect = useCallback(() => {
    setCollected(true);
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (typeof onCollect === "function") {
      onCollect();
    }
  }, [onCollect]);

  const item = useMemo(
    () =>
      new InteractiveItem({
        id,
        radius,
        onCollect: handleCollect,
      }),
    [handleCollect, id, radius]
  );

  useEffect(() => () => item.dispose(), [item]);

  useEffect(() => {
    if (typeof document === "undefined" || !videoSrc) return undefined;

    const video = document.createElement("video");
    video.src = videoSrc;
    video.crossOrigin = "anonymous";
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = "auto";

    const handleLoadedMetadata = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setAspect(video.videoWidth / video.videoHeight);
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    const texture = new THREE.VideoTexture(video);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    if ("colorSpace" in texture) {
      texture.colorSpace = THREE.SRGBColorSpace;
    } else {
      texture.encoding = THREE.sRGBEncoding;
    }

    setVideoTexture(texture);
    videoRef.current = video;

    video.play().catch(() => {
      /* Autoplay restrictions can stop playback; ignore */
    });

    return () => {
      video.pause();
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.src = "";
      video.load();
      texture.dispose();
      setVideoTexture(null);
    };
  }, [videoSrc]);

  const springs = useSpring({
    scale: collected ? 0 : scaleMultiplier,
    opacity: collected ? 0 : 1,
    config: { tension: 240, friction: 32 },
    onRest: () => {
      if (collected) {
        setConsumed(true);
      }
    },
  });

  useFrame((_, delta) => {
    if (consumed) return;

    const mesh = meshRef.current;
    if (!mesh) return;

    mesh.rotation.x = 0;
    mesh.rotation.z = 0;

    mesh.getWorldPosition(worldPos);

    const targetGroup = targetRef?.current?.group;
    if (targetGroup && typeof targetGroup.translation === "function") {
      const translation = targetGroup.translation();
      if (translation) {
        targetPos.set(translation.x, translation.y, translation.z);
        const distance = worldPos.distanceTo(targetPos);
        item.updateDistance(distance);
      }
    }

    if (!camera || collected) return;

    camera.getWorldPosition(cameraWorldPos);

    lookDirection.subVectors(cameraWorldPos, worldPos);
    lookDirection.y = 0;

    if (lookDirection.lengthSq() > 1e-6) {
      lookDirection.normalize();
      const desiredYaw = Math.atan2(lookDirection.x, lookDirection.z);
      const currentYaw = mesh.rotation.y;
      const lerpFactor = 1 - Math.pow(1 - LOOK_LERP_FACTOR, delta * 60);
      mesh.rotation.y = THREE.MathUtils.lerp(currentYaw, desiredYaw, lerpFactor);
    }
  });

  if (consumed) {
    return null;
  }

  const planeHeight = height;
  const planeWidth = aspect * planeHeight;

  return (
    <a.mesh
      ref={meshRef}
      position={position}
      scale={springs.scale.to((s) => [s, s, s])}
      userData={{ name: id }}
    >
      <planeGeometry args={[planeWidth, planeHeight]} />
      <a.meshBasicMaterial
        map={videoTexture}
        transparent
        side={THREE.DoubleSide}
        toneMapped={false}
        alphaTest={0.01}
        opacity={springs.opacity}
      />
    </a.mesh>
  );
}
